import { shouldShowMetric } from '@/payload/collections/metrics/utils';
import type {
  Group,
  Metric,
  Statistic,
  TopicBlock,
} from '@/payload/payload.types';
import { type ParsedChartReturn } from '@tallinn-arvudes/ui/chart/types';
import { parseChartData } from '@tallinn-arvudes/ui/chart/utils';

type Data =
  | {
      type: 'metrics';
      value: ParsedChartReturn;
      raw: Metric;
    }
  | {
      type: 'statistics';
      value: Statistic;
      raw: Statistic;
    };

export type StructuredDocs<WithGroup extends boolean = true> = {
  orderIndex: number;
} & (WithGroup extends true
  ?
      | Data
      | {
          type: 'group';
          value: StructuredDocs<false>[];
          raw?: never;
        }
  : Data);

const parseData = (
  data:
    | { relationTo: 'metrics'; value: number | Metric }
    | { relationTo: 'statistics'; value: number | Statistic },
  extra: { orderIndex: number },
  draft: boolean = false,
): StructuredDocs | null => {
  if (
    !data.value ||
    typeof data.value !== 'object' ||
    !shouldShowMetric(data.value, draft)
  )
    return null;

  if (data.relationTo === 'metrics') {
    return {
      type: 'metrics',
      value: parseChartData(data.value),
      raw: data.value,
      ...extra,
    };
  }

  if (data.relationTo === 'statistics') {
    return {
      type: 'statistics',
      value: data.value,
      raw: data.value,
      ...extra,
    };
  }

  return null;
};

export const structureLinkedDocs = (
  linkedDocs:
    | (
        | { relationTo: 'metrics'; value: number | Metric }
        | { relationTo: 'statistics'; value: number | Statistic }
        | { relationTo: 'groups'; value: number | Group }
      )[]
    | null
    | undefined,
  draft: boolean,
) => {
  if (!linkedDocs) return [] as StructuredDocs[];

  let orderIndex = 0;

  return linkedDocs.reduce((acc, doc) => {
    if (!doc.value || typeof doc.value !== 'object') return acc;

    if (doc.relationTo === 'groups') {
      const structuredData: StructuredDocs<false>[] = [];
      const startingIndex = orderIndex;
      doc.value.linkedDocs?.forEach((linkedDoc, index) => {
        const parsedData = parseData(linkedDoc, {
          orderIndex: orderIndex + index,
        });
        if (parsedData && parsedData.type !== 'group') {
          structuredData.push(parsedData);
          orderIndex++;
        }
      });
      if (structuredData.length > 0) {
        acc.push({
          type: 'group',
          value: structuredData,
          orderIndex: startingIndex,
        });
      }
      return acc;
    }

    if (!shouldShowMetric(doc.value, draft)) {
      return acc;
    }

    const parsedData = parseData(doc, { orderIndex }, draft);

    if (parsedData) {
      acc.push(parsedData);
      orderIndex++;
    }

    return acc;
  }, [] as StructuredDocs[]);
};

export const structureTopicBlockData = ({
  type,
  category,
  linkedDocs,
  title,
  tag,
  overrideTitle,
  overrideTag,
  draft,
}: TopicBlock & {
  id?: string;
  draft: boolean;
}) => {
  if (
    type === 'linked-category' &&
    (!category || typeof category !== 'object')
  ) {
    return {
      tag,
      title,
      data: null,
    };
  }

  if (
    type === 'linked-category' &&
    !!category &&
    typeof category === 'object'
  ) {
    return {
      tag: overrideTag ? tag : category.tag,
      title: overrideTitle ? title : category.name,
      data: structureLinkedDocs(category.linkedDocs, draft),
    };
  }

  return {
    tag,
    title,
    data: structureLinkedDocs(linkedDocs, draft),
  };
};
